// src/pages/CreateIVR.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  RadioGroup,
  Radio,
  Button,
  Collapse,
  Icon,
  FormControl,
  FormLabel,
  Select,
  Checkbox,
  Badge,
  Divider,
  Wrap,
  WrapItem,
  Tooltip,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  SimpleGrid,
  Spinner,
} from "@chakra-ui/react";
import { CheckCircleIcon, EditIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import BaaEntitySelectorModal from "components/BaaEntitySelectorModal";

// --- Cognito admin auth ---
import { useCognito } from "../utils/useCognito";

// --- utils you already have in your codebase ---
import { getMacFromState } from "utils/getMacFromState";
import { assembleAddress } from "utils/assembleAddress";
import macRegions from "utils/macRegions";

// API helpers
import {
  API_URL,
  adminGetUsersList, // add this in commonapicalls.js (POST `${API_URL}/getUser` with bearer)
} from "../utils/commonApiCalls";
import { getBaaDataForUser } from "../utils/commonApiCalls";

// ----------------------------------------------------------------------------------------
// Small shared components (same vibe as your user-facing page)
// ----------------------------------------------------------------------------------------
const StyledInput = ({
  value,
  onChange,
  isRequired = false,
  label,
  containerProps = {},
  ...inputProps
}) => (
  <FormControl isRequired={isRequired} {...containerProps}>
    {label && (
      <FormLabel fontSize="md" isTruncated title={label}>
        {label}
      </FormLabel>
    )}
    <Input
      variant="filled"
      bg="white"
      size="sm"
      fontSize="sm"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        "::placeholder": {
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontSize: "xs",
        },
      }}
      {...inputProps}
    />
  </FormControl>
);

const Section = ({
  title,
  children,
  isOpen,
  onDone,
  onEdit,
  isDone,
  rightAddon,
}) => (
  <Box
    mt={8}
    borderRadius="lg"
    bg="gray.50"
    p={6}
    boxShadow="sm"
    borderWidth={1}
    borderColor="gray.200"
  >
    <HStack justify="space-between" mb={4}>
      <Heading as="h2" size="lg">
        {title}
      </Heading>
      {rightAddon}
    </HStack>
    {isDone && !isOpen ? (
      <Box bg="green.50" p={3} borderRadius="md">
        <HStack justify="space-between">
          <HStack>
            <Icon as={CheckCircleIcon} color="green.500" />
            <Text fontWeight="bold">{title} Complete</Text>
          </HStack>
          <Button onClick={onEdit} leftIcon={<EditIcon />} size="sm" _hover={{ transform: "scale(1)" }}>
            Edit
          </Button>
        </HStack>
      </Box>
    ) : (
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={5} align="stretch">
          {children}
          <Button colorScheme="green" onClick={onDone} alignSelf="center" mt={4} _hover={{ transform: "scale(1)" }}>
            Done
          </Button>
        </VStack>
      </Collapse>
    )}
  </Box>
);

// ----------------------------------------------------------------------------------------
// Wound Form (compact, same required fields as user version)
// ----------------------------------------------------------------------------------------
const WOUND_TYPES = [
  "Diabetic Foot Ulcer",
  "Venous Leg Ulcer",
  "Pressure Ulcer",
  "Traumatic Burns",
  "Radiation Burns",
  "Dehisced Surgical Wound",
  "Necrotizing Faciitis",
  "Other",
];

const WoundForm = ({
  index,
  data,
  onChange,
  replaceWound,
  isOpen,
  onDone,
  onEdit,
  isDone,
  onClearGraftInfo,
}) => {
  const update = (field, value) => {
    const prev = data[field];
    onChange(index, field, value);
    if (["woundLength", "woundWidth", "woundDepth"].includes(field) && value !== prev) {
      setTimeout(() => onClearGraftInfo(index, { [field]: value }), 0);
    }
  };

  const area =
    data.woundLength && data.woundWidth
      ? Number(data.woundLength) * Number(data.woundWidth)
      : null;

  const valid =
    data.woundType &&
    (data.woundType !== "Other" || data.woundTypeOther) &&
    data.woundLength &&
    data.woundWidth &&
    data.applicationDate &&
    data.numApplications &&
    Array.isArray(data.applicationCPT) &&
    data.applicationCPT.length > 0 &&
    data.icd10;

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} mt={6} bg="gray.50" boxShadow="sm">
      <HStack justify="space-between" mb={4}>
        <Heading as="h3" size="md">
          Wound {index + 1}
        </Heading>
        {isDone && !isOpen && (
          <HStack spacing={2}>
            <Icon as={CheckCircleIcon} color="green.500" />
            <Text fontWeight="bold" color="green.600">
              Complete
            </Text>
            <Button size="xs" onClick={onEdit} leftIcon={<EditIcon />} _hover={{ transform: "scale(1)" }}>
              Edit
            </Button>
          </HStack>
        )}
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={6} align="stretch">
          <FormControl isRequired>
            <FormLabel fontWeight="bold">Wound Type</FormLabel>
            <RadioGroup
              value={data.woundType || ""}
              onChange={(val) => update("woundType", val)}
            >
              <Wrap spacing={2}>
                {WOUND_TYPES.map((opt) => {
                  const selected = data.woundType === opt;
                  return (
                    <WrapItem key={opt}>
                      <Box
                        as="label"
                        borderWidth="1px"
                        borderRadius="md"
                        px={3}
                        py={2}
                        bg={selected ? "teal.50" : "white"}
                        borderColor={selected ? "teal.500" : "gray.200"}
                        cursor="pointer"
                        transition="all .15s ease"
                        _hover={{ borderColor: "teal.300" }}
                        onClick={() => update("woundType", opt)}
                      >
                        <Radio value={opt} colorScheme="teal" mr={2}>
                          {opt}
                        </Radio>
                      </Box>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </RadioGroup>
          </FormControl>

          {data.woundType === "Other" && (
            <StyledInput
              placeholder="Please specify wound type"
              value={data.woundTypeOther}
              onChange={(v) => update("woundTypeOther", v)}
              isRequired
            />
          )}

          <HStack align="start" spacing={4} flexWrap="wrap">
            <FormControl isRequired minW="180px">
              <FormLabel fontWeight="bold">Wound Length (cm)</FormLabel>
              <StyledInput
                type="number"
                placeholder="e.g. 4"
                value={data.woundLength || ""}
                onChange={(v) => update("woundLength", v)}
                onWheel={(e) => e.target.blur()}
              />
            </FormControl>

            <FormControl isRequired minW="180px">
              <FormLabel fontWeight="bold">Wound Width (cm)</FormLabel>
              <StyledInput
                type="number"
                placeholder="e.g. 3"
                value={data.woundWidth || ""}
                onChange={(v) => update("woundWidth", v)}
                onWheel={(e) => e.target.blur()}
              />
            </FormControl>

            <FormControl minW="180px">
              <FormLabel fontWeight="bold">Wound Depth (cm)</FormLabel>
              <StyledInput
                type="number"
                placeholder="e.g. 0.5"
                value={data.woundDepth || ""}
                onChange={(v) => update("woundDepth", v)}
                onWheel={(e) => e.target.blur()}
              />
            </FormControl>

            {area != null && !isNaN(area) && (
              <Badge alignSelf="end" colorScheme="teal" variant="subtle" fontSize="sm" mt="30px">
                Area: {Number(area).toFixed(2)} cm²
              </Badge>
            )}
          </HStack>

          <FormControl isRequired>
            <Checkbox
              isChecked={data.priorAuthRequired || false}
              onChange={(e) => update("priorAuthRequired", e.target.checked)}
              colorScheme="teal"
              sx={{
                "& .chakra-checkbox__control": {
                  bg: "white",
                  borderColor: "gray",
                  transition: "all 0.2s",
                  _checked: { bg: "teal.500", borderColor: "teal.500" },
                },
              }}
            >
              <Text as="span" fontWeight="bold">
                Prior Authorization Required
              </Text>
              <Tooltip
                label="If checked, please attach clinical documentation after submission."
                fontSize="xs"
                hasArrow
              >
                <InfoOutlineIcon ml={2} color="gray.500" />
              </Tooltip>
            </Checkbox>
          </FormControl>

          <Divider />

          <HStack spacing={4} flexWrap="wrap">
            <FormControl isRequired minW="220px">
              <FormLabel fontWeight="bold">Date of Application</FormLabel>
              <StyledInput
                type="date"
                value={data.applicationDate || ""}
                onChange={(v) => update("applicationDate", v)}
                width="fit-content"
              />
            </FormControl>

            <FormControl isRequired minW="220px">
              <FormLabel fontWeight="bold">Anticipated Number of Applications</FormLabel>
              <StyledInput
                type="number"
                min={1}
                value={data.numApplications || ""}
                onChange={(v) => update("numApplications", v)}
                width="fit-content"
              />
            </FormControl>
          </HStack>

          {/* CPT compact picker */}
          <Box>
            <Text fontWeight="bold" mb={3}>Application CPT Codes *</Text>
            <Text fontSize="sm" color="gray.600" mb={4}>Click to select/deselect CPT codes:</Text>
            <HStack spacing={3} flexWrap="wrap">
              {["15271","15272","15273","15274","15275","15276","15277","15278"].map(code => {
                const current = Array.isArray(data.applicationCPT) ? data.applicationCPT : [];
                const isSelected = current.includes(code);
                const next = isSelected ? current.filter(c => c !== code) : [...current, code];
                return (
                  <Button
                    key={code}
                    size="md"
                    variant="solid"
                    colorScheme={isSelected ? "green" : "gray"}
                    onClick={() => update("applicationCPT", next)}
                    minW="80px"
                    fontWeight="bold"
                    _hover={{ transform: "scale(1.00)", textColor: "black", bg: "white" }}
                  >
                    {code}
                  </Button>
                );
              })}
            </HStack>
          </Box>

          <FormControl isRequired>
            <FormLabel fontWeight="bold">ICD-10 Diagnosis Codes</FormLabel>
            <StyledInput
              placeholder="ICD-10 Diagnosis Codes"
              value={data.icd10 || ""}
              onChange={(v) => update("icd10", v)}
            />
          </FormControl>

          <Checkbox
            isChecked={data.inSNF || false}
            onChange={(e) => update("inSNF", e.target.checked)}
            colorScheme="teal"
            sx={{
              "& .chakra-checkbox__control": {
                bg: "white",
                borderColor: "gray",
                transition: "all 0.2s",
                _checked: { bg: "teal.500", borderColor: "teal.500" },
              },
            }}
          >
            Patient currently residing in SNF?
          </Checkbox>

          <Checkbox
            isChecked={data.inGlobalPeriod || false}
            onChange={(e) => update("inGlobalPeriod", e.target.checked)}
            colorScheme="teal"
            sx={{
              "& .chakra-checkbox__control": {
                bg: "white",
                borderColor: "gray",
                transition: "all 0.2s",
                _checked: { bg: "teal.500", borderColor: "teal.500" },
              },
            }}
          >
            Patient under a surgical Global Period?
          </Checkbox>

          {data.inGlobalPeriod && (
            <>
              <FormControl>
                <FormLabel fontWeight="bold">Related Surgical CPT</FormLabel>
                <StyledInput
                  placeholder="Related Surgical CPT"
                  value={data.globalPeriodCPT || ""}
                  onChange={(v) => update("globalPeriodCPT", v)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Related Surgical Date</FormLabel>
                <StyledInput
                  type="date"
                  value={data.globalPeriodDate || ""}
                  onChange={(v) => update("globalPeriodDate", v)}
                  width="fit-content"
                />
              </FormControl>
            </>
          )}

          <Divider />

          <Button
            colorScheme="teal"
            size="md"
            alignSelf="flex-start"
            onClick={onDone}
            isDisabled={!valid}
            _hover={{ transform: "scale(1)" }}
          >
            Done
          </Button>
        </VStack>
      </Collapse>
    </Box>
  );
};

// ----------------------------------------------------------------------------------------
// Main: CreateIVR (Admin, Cognito)
// ----------------------------------------------------------------------------------------
const isBlank = (v) => v == null || `${v}`.trim() === "";
const safeString = (val) => (val != null ? `${val}` : "");

const CreateIVR = () => {
  const { getIdToken } = useCognito(); // ✅ Cognito (admin side)
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // --- Auth token (no conditional hooks)
  const [token, setToken] = useState("");
  const [authReady, setAuthReady] = useState(false);

  // --- Admin "act on behalf" user list + selection
  const [usersLoading, setUsersLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState("");
  const [targetUserEmail, setTargetUserEmail] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [baaData, setBaaData] = useState(null);
  const [loadingBaa, setLoadingBaa] = useState(false);

  // keep a ref to the latest baaData so modal selection handlers can read freshest value
  const baaDataRef = React.useRef(baaData);
  useEffect(() => { baaDataRef.current = baaData; }, [baaData]);

  // modals: facility / physician / patient
  const [openFacilityModal, setOpenFacilityModal] = useState(false);
  const [openPhysicianModal, setOpenPhysicianModal] = useState(false);
  const [openPatientModal, setOpenPatientModal] = useState(false);

  // Preselect via ?email=...
  const preselectEmail = searchParams.get("email") || "";

  // --- Section step management
  const steps = ["Facility", "Physician", "Patient", "Insurance", "Wounds"];
  const [currentStep, setCurrentStep] = useState(0);
  const SectionVisible = (step) => steps[currentStep] === step;
  const [completed, setCompleted] = useState({});

  // --- Selected wound panel
  const [selectedWoundIndex, setSelectedWoundIndex] = useState(0);

  // --- Errors and server loading
  const [errorMessages, setErrorMessages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // --- Flags from URL
  const cloneFrom = searchParams.get("cloneFrom") || "";
  const fromCalculator = !!(
    searchParams.get("product") ||
    searchParams.get("size") ||
    searchParams.get("needed") ||
    searchParams.get("area")
  );

  // --- IVR Form Data
  const initialFormData = useMemo(
    () => ({
      facility: {
        street: "",
        city: "",
        state: "",
        zip: "",
        mac: "",
        name: "",
        phone: "",
        fax: "",
        contactName: "",
        npi: "",
        tin: "",
        ptan: "",
        jurisdiction: "",
        address: "",
      },
      physician: {
        street: "",
        city: "",
        state: "",
        zip: "",
        mac: "",
        name: "",
        phone: "",
        fax: "",
        contact: "",
        contactPhone: "",
        npi: "",
        tin: "",
        ptan: "",
        specialty: "",
        siteName: "",
        jurisdiction: "",
        address: "",
      },
      patient: {
        street: "",
        city: "",
        state: "",
        zip: "",
        name: "",
        dob: "",
        phone: "",
        okToContact: "",
        address: "",
      },
      insurance: [{}],
      wounds: [{ inSNF: false, inGlobalPeriod: false, applicationCPT: [] }],
      graftsUsed: [],
      salesRepresentative: "",
      posType: "",
      posOtherText: "",
      formType: "",
      isCloneOfIvrId: cloneFrom || null,
      applicationNumber: 1,
      numWounds: 1,
    }),
    [cloneFrom]
  );

  const [formData, setFormData] = useState(initialFormData);

  // --- Wounds numeric controls (no conditional hooks)
  const [numWoundsInput, setNumWoundsInput] = useState(() =>
    (fromCalculator || !!cloneFrom) ? "1" : (initialFormData.numWounds?.toString() || "1")
  );
  const [lastValidNumWounds, setLastValidNumWounds] = useState(() =>
    Number.isInteger(initialFormData.numWounds) && initialFormData.numWounds > 0
      ? initialFormData.numWounds
      : 1
  );

  // ──────────────────────────────────────────────────────────────
  // Auth bootstrap (Cognito) and user list load
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const t = getIdToken();
      if (t) setToken(t);
    } catch (e) {
      console.error("Cognito getIdToken failed", e);
    } finally {
      setAuthReady(true);
    }
  }, [getIdToken]);

  useEffect(() => {
    if (!authReady) return;
    (async () => {
      setUsersLoading(true);
      try {
        const t = getIdToken();
        if (!t) throw new Error("Not authenticated");
        const list = await adminGetUsersList(t);
        const normalized = list.map((u) => ({
          UserId: u.UserId ?? u.userId ?? u.id ?? "",
          email: u.email ?? "",
          FirstName: u.FirstName ?? "",
          LastName: u.LastName ?? "",
          // include facility info if present so the UI can search by facility name
          facility: (u.facility && typeof u.facility === 'object') ? u.facility : (u.facilityName ? { name: u.facilityName } : undefined),
        }));
        setUsers(normalized);

        if (preselectEmail) {
          const found = normalized.find(
            (u) => (u.email || "").toLowerCase() === preselectEmail.toLowerCase()
          );
          if (found) {
            setTargetUserId(found.UserId);
            setTargetUserEmail(found.email);
          }
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        // Non-fatal; allow manual entry
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [authReady, getIdToken, preselectEmail]);

  // When targetUserId or targetUserEmail changes, fetch BAA data for that user (if available)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setBaaData(null);
      if (!token) return;
      const id = targetUserId || undefined;
      const email = !targetUserId && targetUserEmail ? targetUserEmail : undefined;
      if (!id && !email) return;
      setLoadingBaa(true);
      try {
        const data = await getBaaDataForUser(id || email, token);
        if (!mounted) return;
        setBaaData(data || null);
      } catch (err) {
        console.error("Error loading BAA data:", err);
      } finally {
        if (mounted) setLoadingBaa(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [targetUserId, targetUserEmail, token]);

  // Keep email in sync when selecting from dropdown
  useEffect(() => {
    if (!targetUserId || users.length === 0) return;
    const match = users.find((u) => u.UserId === targetUserId);
    setTargetUserEmail(match?.email || "");
  }, [targetUserId, users]);

  // ──────────────────────────────────────────────────────────────
  // Helpers (unchanged logic)
  // ──────────────────────────────────────────────────────────────
  const updateSection = (section, field, value) => {
    setFormData((prev) => {
      const updatedSection = { ...prev[section], [field]: value };

      if (field === "state") {
        const match = macRegions.find(
          (region) => region.abbreviation === value || region.state === value
        );
        if (match) {
          updatedSection.mac = match.macName;
          updatedSection.jurisdiction = match.jurisdiction;
        } else {
          updatedSection.mac = "";
          updatedSection.jurisdiction = "";
        }
      }

      if (["street", "city", "state", "zip"].includes(field)) {
        const { street = "", city = "", state = "", zip = "" } = updatedSection;
        updatedSection.address = assembleAddress({ street, city, state, zip });
      }

      return { ...prev, [section]: updatedSection };
    });
  };

  const prettyField = (key) =>
    ({
      name: "Name",
      phone: "Phone",
      fax: "Fax",
      contactName: "Contact Name",
      contactPhone: "Contact Phone",
      street: "Street",
      city: "City",
      state: "State",
      zip: "Zip Code",
      npi: "NPI",
      tin: "TIN",
      ptan: "PTAN",
      siteName: "Site Name",
      dob: "Date of Birth",
      okToContact: "OK to Contact",
      policy: "Policy Number",
      subscriberName: "Subscriber Name",
      subscriberDob: "Subscriber DOB",
      planType: "Plan Type",
      formType: "Type of Submission",
      posType: "Place of Service (POS)",
    }[key] || key);

  const validateSectionFields = (obj, requiredKeys) =>
    requiredKeys.filter((key) => !obj?.[key]).map((key) => prettyField(key));

  const validateFacility = () => {
    const posType = formData.posType || "";
    const base = { ...formData.facility, posType };
    if (posType === "Other" || posType === "POSOther") base.posOtherText = formData.posOtherText;

    return validateSectionFields(base, [
      "name",
      "phone",
      "fax",
      "contactName",
      "street",
      "city",
      "state",
      "zip",
      "npi",
      "tin",
      "ptan",
      "posType",
      ...(posType === "Other" || posType === "POSOther" ? ["posOtherText"] : []),
    ]);
  };

  const validatePhysician = () =>
    validateSectionFields(formData.physician, [
      "name",
      "phone",
      "contact",
      "contactPhone",
      "street",
      "city",
      "state",
      "zip",
      "npi",
      "tin",
      "ptan",
    ]);

  const validatePatient = () => {
    const missing = validateSectionFields(formData.patient, [
      "name",
      "dob",
      "phone",
      "street",
      "city",
      "state",
      "zip",
      "okToContact",
    ]);
    if (!formData.formType) missing.push("Type of Submission");
    return missing;
  };

  const validateInsurance = () =>
    formData.insurance.flatMap((entry, index) =>
      validateSectionFields(entry, [
        "name",
        "policy",
        "subscriberName",
        "subscriberDob",
        "planType",
        "phone",
        "inNetwork",
      ]).map((field) => `${["Primary", "Secondary", "Tertiary"][index]} ${field}`)
    );

  const updateInsurance = (index, field, value) => {
    const updated = [...formData.insurance];
    updated[index] = { ...updated[index], [field]: value };
    setFormData((prev) => ({ ...prev, insurance: updated }));
  };

  const handleAddInsurance = () => {
    if (formData.insurance.length < 3) {
      const patientName = formData?.patient?.name || "";
      const patientDob = formData?.patient?.dob || "";
      setFormData((prev) => ({
        ...prev,
        insurance: [
          ...prev.insurance,
          { subscriberName: patientName, subscriberDob: patientDob },
        ],
      }));
    }
  };

  const handleRemoveInsurance = (index) => {
    const updated = formData.insurance.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, insurance: updated }));
  };

  // Auto-fill insurance subscriberName and subscriberDob from patient when user finishes editing
  // (we call this onBlur so we don't copy partial/typed values).
  const autofillInsuranceFromPatient = () => {
    const patientName = formData.patient?.name || "";
    const patientDob = formData.patient?.dob || "";
    if (!patientName && !patientDob) return; // nothing to copy

    setFormData((prev) => {
      const insurance = (prev.insurance || []).map((entry) => {
        const next = { ...entry };
        if (!next.subscriberName && patientName) next.subscriberName = patientName;
        if (!next.subscriberDob && patientDob) next.subscriberDob = patientDob;
        return next;
      });
      return { ...prev, insurance };
    });
  };

  // --- Wounds list management
const updateNumWounds = (value) => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed <= 0 || parsed > 10) return;

  const existing = formData.wounds || [];
  const newArray = [...existing];

  // grow to parsed
  while (newArray.length < parsed) {
    newArray.push({
      applicationCPT: [],
      isDone: false,
      inSNF: false,
      inGlobalPeriod: false,
    });
  }
  // shrink to parsed
  while (newArray.length > parsed) newArray.pop();

  setFormData((prev) => ({
    ...prev,
    numWounds: parsed,
    wounds: newArray,
  }));

  // refresh completion map and selection
  const newCompleted = {};
  for (let i = 0; i < parsed; i++) newCompleted[i] = !!newArray[i]?.isDone;
  setCompleted(newCompleted);
  setSelectedWoundIndex(0);
  setLastValidNumWounds(parsed);
};

  const syncGraftsUsed = (wounds) =>
    wounds
      .filter((w) => w.graft && w.graft.qCode && w.graft.size && w.graft.quantity)
      .map((w) => ({
        product: w.graft.qCode,
        size: w.graft.size,
        quantity: w.graft.quantity,
      }));

  const updateWoundField = (index, field, value) => {
    const updated = [...(formData.wounds || [])];
    if (field === "applicationCPT") {
      updated[index] = { ...updated[index], [field]: Array.isArray(value) ? value : value ? [value] : [] };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    const updatedGraftsUsed = syncGraftsUsed(updated);
    setFormData((prev) => ({ ...prev, wounds: updated, graftsUsed: updatedGraftsUsed }));
    setSelectedWoundIndex(index);
  };

  const replaceWound = (index, newWoundData) => {
    const updated = [...formData.wounds];
    updated[index] = newWoundData;
    const updatedGraftsUsed = syncGraftsUsed(updated);
    setFormData((prev) => ({ ...prev, wounds: updated, graftsUsed: updatedGraftsUsed }));
  };

  const handleWoundDone = (i) => {
    const w = formData.wounds?.[i];
    const isValid =
      w &&
      w.woundType &&
      (w.woundType !== "Other" || w.woundTypeOther) &&
      w.woundLength &&
      w.woundWidth &&
      w.applicationDate &&
      w.numApplications &&
      Array.isArray(w.applicationCPT) &&
      w.applicationCPT.length > 0 &&
      w.icd10;

    if (!isValid) {
      toast({
        title: `Wound ${i + 1} Incomplete`,
        description: "Please complete all required fields before continuing.",
        status: "warning",
        duration: 7000,
        isClosable: true,
      });
      return;
    }

    setCompleted((prev) => ({ ...prev, [i]: true }));
    setFormData((prev) => {
      const newWounds = [...prev.wounds];
      newWounds[i].isDone = true;
      return { ...prev, wounds: newWounds };
    });

    if (i + 1 < (formData.wounds?.length || 0)) {
      setSelectedWoundIndex(i + 1);
    } else {
      setSelectedWoundIndex(null);
    }
  };

  const handleWoundEdit = (i) => {
    setCompleted((prev) => ({ ...prev, [i]: false }));
    setFormData((prev) => {
      const newWounds = [...prev.wounds];
      newWounds[i].isDone = false;
      return { ...prev, wounds: newWounds };
    });
    setSelectedWoundIndex(i);
  };

  const handleClearGraftInfo = (index, override = {}) => {
    const updatedWounds = [...formData.wounds];
    updatedWounds[index] = {
      ...updatedWounds[index],
      ...override,
      graft: null,
      hcpcs: "",
    };
    setFormData((prev) => ({ ...prev, wounds: updatedWounds }));
  };

  const handleDone = (step) => {
    setCompleted((prev) => ({ ...prev, [step]: true }));
    const nextIndex = steps.indexOf(step) + 1;
    setCurrentStep(nextIndex < steps.length ? nextIndex : steps.length - 1);
  };

  const buildFinalFormData = (data) => {
    const buildAddressFields = (section = {}) => {
      const { street = "", city = "", state = "", zip = "" } = section;
      return {
        address: assembleAddress({ street, city, state, zip }),
        address_full: { street, city, state, zip },
      };
    };

    return {
      ...data,
      facility: { ...data.facility, ...buildAddressFields(data.facility) },
      physician: { ...data.physician, ...buildAddressFields(data.physician) },
      patient: { ...data.patient, ...buildAddressFields(data.patient) },
    };
  };

  const submitToServer = async () => {
    const missingFacility = validateFacility();
    if (missingFacility.length) {
        toast({
        title: "Facility incomplete",
        description: `Missing: ${missingFacility.join(", ")}`,
        status: "warning",
        duration: 7000,
        isClosable: true,
        });
        setCurrentStep(0);
        return;
    }
    const missingDoc = validatePhysician();
    if (missingDoc.length) {
        toast({
        title: "Physician incomplete",
        description: `Missing: ${missingDoc.join(", ")}`,
        status: "warning",
        duration: 7000,
        isClosable: true,
        });
        setCurrentStep(1);
        return;
    }
    const missingPatient = validatePatient();
    if (missingPatient.length) {
        toast({
        title: "Patient incomplete",
        description: `Missing: ${missingPatient.join(", ")}`,
        status: "warning",
        duration: 7000,
        isClosable: true,
        });
        setCurrentStep(2);
        return;
    }
    const missingIns = validateInsurance();
    if (missingIns.length) {
        toast({
        title: "Insurance incomplete",
        description: `Missing: ${missingIns.join(", ")}`,
        status: "warning",
        duration: 7000,
        isClosable: true,
        });
        setCurrentStep(3);
        return;
    }

    const anyWoundInvalid = (formData.wounds || []).some((w) => !(
        w &&
        w.woundType &&
        (w.woundType !== "Other" || w.woundTypeOther) &&
        w.woundLength &&
        w.woundWidth &&
        w.applicationDate &&
        w.numApplications &&
        Array.isArray(w.applicationCPT) &&
        w.applicationCPT.length > 0 &&
        w.icd10
    ));
    if (anyWoundInvalid) {
        toast({
        title: "Wounds incomplete",
        description: "Please complete all required wound fields.",
        status: "warning",
        duration: 7000,
        isClosable: true,
        });
        setCurrentStep(4);
        return;
    }

    if (isBlank(targetUserId) && isBlank(targetUserEmail)) {
        toast({
        title: "Missing target user",
        description: "Select a user from the list or enter a UserId/Email.",
        status: "info",
        duration: 6000,
        isClosable: true,
        });
        return;
    }

    setSubmitting(true);
    setErrorMessages([]);

    try {
        const finalFormData = buildFinalFormData(formData);

        // ✅ Admin endpoint expects TOP-LEVEL IVR fields and `userId`
        const url = `https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createIvr`;

        const payload = {
        userId: targetUserId || undefined,
        // If you later support email lookup on the backend, you can add:
        // email: targetUserEmail || undefined,

        // Spread the IVR fields at the top level (no nested `data` object)
        ...finalFormData,
        };

        const res = await axios.post(url, payload, {
        headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
        },
        });

        const { success, ivrId, message, errors } = res.data || {};
        if (!success) {
        if (errors && Array.isArray(errors)) setErrorMessages(errors);
        throw new Error(message || "Create IVR failed");
        }

        toast({
        title: "IVR created",
        description: ivrId ? `ID: ${ivrId}` : undefined,
        status: "success",
        duration: 6000,
        isClosable: true,
        });

        navigate(
        ivrId
            ? `/portal/ivr-status?ivr=${encodeURIComponent(ivrId)}`
            : "/portal/ivr-status"
        );
    } catch (err) {
        if (err?.response?.status === 400) {
        setErrorMessages(err.response.data?.errors || []);
        toast({
            title: "Validation Error",
            description: "Please fix the errors below and try again.",
            status: "error",
            duration: 9000,
            isClosable: true,
        });
        } else {
        toast({
            title: "Server Error",
            description: "The server is having issues, please try again later.",
            status: "error",
            duration: 9000,
            isClosable: true,
        });
        }
        console.error("Admin Create IVR error:", err);
    } finally {
        setSubmitting(false);
    }
    };


  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────
  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg">Create IVR (Admin)</Heading>
        <Badge colorScheme="purple" variant="subtle">Acting on behalf of a user</Badge>
      </Flex>

      {/* Acting As user picker */}
      <Box mb={6} p={4} bg="purple.50" border="1px solid #D6BCFA" borderRadius="md">
        <Text fontWeight="bold" mb={2}>Target User</Text>
        <Text fontSize="sm" color="gray.700" mb={3}>
          Select a user (email — UserId), or enter a UserId/Email manually.
        </Text>

        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          {/* Typeahead search (and keep the original Select) */}
          <FormControl>
            <FormLabel>Search user by email / UserId / name / facility</FormLabel>
            <Box position="relative">
              <Input
                variant="filled"
                bg="white"
                size="sm"
                placeholder={usersLoading ? "Loading users…" : "Start typing to search users"}
                value={userQuery}
                onChange={(e) => {
                  setUserQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />

              {showSuggestions && userQuery && !usersLoading && (
                <Box
                  position="absolute"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="md"
                  mt={2}
                  width="100%"
                  maxH="220px"
                  overflowY="auto"
                  zIndex={50}
                  borderRadius="md"
                >
                  {users
                    .filter((u) => {
                      const q = userQuery.toLowerCase().trim();
                      if (!q) return false;
                      const email = (u.email || "").toLowerCase();
                      const id = (u.UserId || u.userId || "").toLowerCase();
                      const name = ((u.FirstName || "") + " " + (u.LastName || "")).toLowerCase();
                      const facility = ((u.facility && (u.facility.name || u.facilityName)) || "").toLowerCase();
                      return (
                        email.includes(q) ||
                        id.includes(q) ||
                        name.includes(q) ||
                        facility.includes(q)
                      );
                    })
                    .slice(0, 30)
                    .map((u) => (
                      <Box
                        key={u.UserId || u.userId || `${u.email}-${Math.random()}`}
                        px={3}
                        py={2}
                        _hover={{ bg: "gray.50", cursor: "pointer" }}
                        onMouseDown={(e) => {
                          // use onMouseDown to avoid blur before click
                          setTargetUserId(u.UserId || u.userId || "");
                          setTargetUserEmail(u.email || "");
                          setUserQuery(u.email || u.UserId || "");
                          setShowSuggestions(false);
                        }}
                      >
                        <Text fontWeight="bold">{u.email || "(no email)"}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {u.UserId || u.userId} {u.FirstName || u.LastName ? `— ${u.FirstName || ""} ${u.LastName || ""}` : ""}
                          {u.facility?.name || u.facilityName ? ` — ${u.facility?.name || u.facilityName}` : ""}
                        </Text>
                      </Box>
                    ))}
                </Box>
              )}
                {/* BAA entity selector modals */}
                <BaaEntitySelectorModal
                  isOpen={openFacilityModal}
                  onClose={() => { setOpenFacilityModal(false); BaaEntitySelectorModal.setOnSelect(() => {}); }}
                  entityType="facility"
                  baaData={baaData}
                  selectedEntity={formData.facility}
                />

                <BaaEntitySelectorModal
                  isOpen={openPhysicianModal}
                  onClose={() => { setOpenPhysicianModal(false); BaaEntitySelectorModal.setOnSelect(() => {}); }}
                  entityType="physician"
                  baaData={baaData}
                  selectedEntity={formData.physician}
                />

                <BaaEntitySelectorModal
                  isOpen={openPatientModal}
                  onClose={() => { setOpenPatientModal(false); BaaEntitySelectorModal.setOnSelect(() => {}); }}
                  entityType="patient"
                  baaData={baaData}
                  selectedEntity={formData.patient}
                />
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel>User (email → UserId) Dropdown</FormLabel>
            <Select
              placeholder={usersLoading ? "Loading users…" : "Select a user"}
              value={targetUserId}
              onChange={(e) => {
                setTargetUserId(e.target.value);
                const found = users.find((u) => (u.UserId || u.userId) === e.target.value);
                if (found) {
                  setTargetUserEmail(found.email || "");
                  setUserQuery(found.email || found.UserId || "");
                }
              }}
              bg="white"
              isDisabled={usersLoading}
            >
              {users.map((u) => (
                <option key={u.UserId} value={u.UserId}>
                  {(u.email || "(no email)")} → {u.UserId}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Selected Email (or override)</FormLabel>
            <Input
              variant="filled"
              bg="white"
              size="sm"
              value={targetUserEmail}
              onChange={(e) => setTargetUserEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </FormControl>
        </SimpleGrid>

        {!usersLoading && isBlank(targetUserId) && isBlank(targetUserEmail) && (
          <Alert status="info" mt={3}>
            <AlertIcon />
            You can still fill the form, but you must select or enter a user before submitting.
          </Alert>
        )}
      </Box>

      {!authReady ? (
        <Flex align="center" gap={3} p={6}>
          <Spinner />
          <Text>Initializing…</Text>
        </Flex>
      ) : (
        <>
          {/* Facility */}
          <Section
            title="Facility Information"
            isOpen={SectionVisible("Facility")}
            isDone={completed.Facility}
            onDone={() => {
              const errors = validateFacility();
              if (errors.length > 0) {
                toast({
                  title: "Please complete all required fields",
                  description: `Missing: ${errors.join(", ")}`,
                  status: "warning",
                  duration: 7000,
                  isClosable: true,
                });
                return;
              }
              handleDone("Facility");
              setCurrentStep(1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onEdit={() => setCurrentStep(0)}
            rightAddon={
              <Button size="sm" variant="ghost" onClick={() => {
                // wire modal selection to fill facility fields
                BaaEntitySelectorModal.setOnSelect((f) => {
                  if (!f) return;
                  // common fields
                  updateSection("facility", "name", f.name || f.facilityName || "");
                  updateSection("facility", "phone", f.phone || "");
                  updateSection("facility", "fax", f.fax || "");
                  updateSection("facility", "street", f.street || f.address_line1 || "");
                  updateSection("facility", "city", f.city || "");
                  updateSection("facility", "state", f.state || "");
                  updateSection("facility", "zip", f.zip || f.postalCode || "");

                  // Contact name: prefer top-level primaryContactName from BAA, then facility-level fallbacks
                  const primaryContact = (baaData && (baaData.primaryContactName || baaData.primary_contact_name)) || f.primaryContactName || f.primary_contact_name || f.contactName || f.contact?.name || "";
                  if (primaryContact) updateSection("facility", "contactName", primaryContact);

                  // Identifiers: NPI, TIN, PTAN - prefer facility-level then top-level baa fallbacks
                  const npi = f.npi || f.facilityNPI || baaData?.facilityNPI || f.npiNumber || "";
                  const tin = f.tin || f.facilityTIN || baaData?.facilityTIN || "";
                  const ptan = f.ptan || f.facilityPTAN || baaData?.facilityPTAN || "";
                  if (npi) updateSection("facility", "npi", `${npi}`);
                  if (tin) updateSection("facility", "tin", `${tin}`);
                  if (ptan) updateSection("facility", "ptan", `${ptan}`);

                  // Place of Service: try several common keys and map to the form's POS codes if reasonable
                  // Prefer baaData.selectedFacility (backend canonical) when present. Use the live ref to avoid stale closure values.
                  const liveBaa = baaDataRef.current || {};
                  const rawPos = (liveBaa?.selectedFacility || f.posType || f.placeOfService || f.pos || f.place_of_service || liveBaa?.facilityPOS || liveBaa?.posType || "");
                  if (rawPos) {
                    let posStr = `${rawPos}`.trim();
                    // If it's like 'Physician Office (11)' extract the numeric code
                    const parenMatch = posStr.match(/\((\d{1,2})\)/);
                    if (parenMatch) posStr = parenMatch[1];

                    const mapPos = (val) => {
                      if (!val) return "";
                      const v = `${val}`.trim();
                      // already in POSxx form
                      if (/^POS\d{1,2}$/i.test(v)) return v.toUpperCase();
                      // numeric like '11' or 11 -> POS11
                      const numMatch = v.match(/^(\d{1,2})$/);
                      if (numMatch) return `POS${numMatch[1]}`;
                      // sometimes backend sends 'Physician Office' or 'Office - 11' etc.
                      const lower = v.toLowerCase();
                      if (lower.includes('physician') && lower.includes('office')) return 'POS11';
                      if (lower.includes('office') && lower.includes('11')) return 'POS11';
                      if (lower.includes('home')) return 'POS12';
                      if (lower.includes('hospital') && lower.includes('outpatient')) return 'POS22';
                      if (lower.includes('ambulatory') || lower.includes('surgical center')) return 'POS24';
                      if (lower.includes('skilled') && lower.includes('nursing')) return 'POS32';
                      if (lower.includes('mobile') && lower.includes('nursing')) return 'POS32';
                      if (lower.includes('other')) return 'POSOther';
                      return "";
                    };
                    const mapped = mapPos(posStr);
                    if (mapped) setFormData((prev) => ({ ...prev, posType: mapped }));
                  }
                });
                setOpenFacilityModal(true);
              }}
              sx={{
                bg: 'blue.200',
                border: '1px solid',
                borderColor: 'gray.200',
                _hover: { bg: 'gray.50' },
                borderRadius: 'md'
              }}
              >Select Facility</Button>
            }
          >
            <HStack>
              <FormControl isRequired>
                <FormLabel>Place of Service (POS)</FormLabel>
                <Select
                  placeholder="Select POS"
                  value={formData.posType || ""}
                  bg="white"
                  focusBorderColor="teal.400"
                  onChange={(e) => {
                    const selected = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      posType: selected,
                      posOtherText:
                        selected === "POSOther" || selected === "Other" ? prev.posOtherText : "",
                    }));
                  }}
                >
                  <option value="POS11">Physician Office (11)</option>
                  <option value="POS12">Home (12)</option>
                  <option value="POS22">Hospital Outpatient (22)</option>
                  <option value="POS24">Ambulatory Surgical Center (24)</option>
                  <option value="POS32">Mobile Nursing Home (32)</option>
                  <option value="POSOther">Other</option>
                </Select>
              </FormControl>
              {(formData.posType === "POSOther" || formData.posType === "Other") && (
                <StyledInput
                  label="Please specify other POS"
                  placeholder="e.g., Critical Access Hospital"
                  value={formData.posOtherText}
                  onChange={(v) =>
                    setFormData((prev) => ({ ...prev, posOtherText: v }))
                  }
                  isRequired
                />
              )}
            </HStack>

            <HStack>
              <StyledInput
                label="Facility Name"
                value={formData.facility?.name}
                onChange={(v) => updateSection("facility", "name", v)}
                isRequired
              />
              <StyledInput
                label="Phone"
                type="tel"
                inputMode="numeric"
                value={formData.facility?.phone}
                onChange={(v) => updateSection("facility", "phone", v)}
                isRequired
              />
              <StyledInput
                label="Fax"
                type="tel"
                inputMode="numeric"
                value={formData.facility?.fax}
                onChange={(v) => updateSection("facility", "fax", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Street"
                value={formData.facility?.street}
                onChange={(v) => updateSection("facility", "street", v)}
                isRequired
              />
              <StyledInput
                label="City"
                value={formData.facility?.city}
                onChange={(v) => updateSection("facility", "city", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="State"
                value={formData.facility?.state}
                onChange={(v) => {
                  updateSection("facility", "state", v);
                  const mac = getMacFromState(v);
                  updateSection("facility", "mac", mac || "");
                }}
                isRequired
              />
              <StyledInput
                label="Zip Code"
                value={formData.facility?.zip}
                onChange={(v) => updateSection("facility", "zip", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Contact Name"
                value={formData.facility?.contactName}
                onChange={(v) => updateSection("facility", "contactName", v)}
                isRequired
              />
              <StyledInput
                label="Medicare Admin Contractor"
                value={formData.facility?.mac}
                onChange={(v) => updateSection("facility", "mac", v)}
                isRequired
                isDisabled
              />
              <StyledInput
                label="Jurisdiction"
                value={formData.facility?.jurisdiction}
                onChange={(v) => updateSection("facility", "jurisdiction", v)}
                isRequired
                isDisabled
              />
            </HStack>
            <HStack>
              <StyledInput
                label="NPI"
                value={formData.facility?.npi}
                onChange={(v) => updateSection("facility", "npi", v)}
                isRequired
              />
              <StyledInput
                label="TIN"
                value={formData.facility?.tin}
                onChange={(v) => updateSection("facility", "tin", v)}
                isRequired
              />
              <StyledInput
                label="PTAN"
                value={formData.facility?.ptan}
                onChange={(v) => updateSection("facility", "ptan", v)}
                isRequired
              />
            </HStack>
          </Section>

          {/* Physician */}
          <Section
            title="Physician Information"
            isOpen={SectionVisible("Physician")}
            isDone={completed.Physician}
            onDone={() => {
              const errors = validatePhysician();
              if (errors.length > 0) {
                toast({
                  title: "Please complete all required fields",
                  description: `Missing: ${errors.join(", ")}`,
                  status: "warning",
                  duration: 7000,
                  isClosable: true,
                });
                return;
              }
              handleDone("Physician");
              setCurrentStep(2);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onEdit={() => setCurrentStep(1)}
            rightAddon={
              <Button size="sm" variant="ghost" onClick={() => {
                BaaEntitySelectorModal.setOnSelect((p) => {
                  if (!p) return;
                  // map common fields
                  const name = `${p.physicianFirstName || p.firstName || ""} ${p.physicianLastName || p.lastName || ""}`.trim();
                  updateSection("physician", "name", name);
                  updateSection("physician", "phone", p.phone || p.contactPhone || "");
                  updateSection("physician", "fax", p.fax || "");
                  updateSection("physician", "npi", p.npi || p.npiNumber || "");
                  updateSection("physician", "street", p.street || "");
                  updateSection("physician", "city", p.city || "");
                  updateSection("physician", "state", p.state || "");
                  updateSection("physician", "zip", p.zip || "");

                  // Extra fields: specialty, tin, ptan, contact name/phone, siteName
                  const livePhys = (baaData && baaData.physicianInfo) || {};
                  const specialty = p.specialty || p.speciality || livePhys.specialty || "";
                  const tin = p.tin || p.tinNumber || livePhys.tin || p.tin || "";
                  const ptan = p.ptan || livePhys.ptan || "";
                  const contactName = p.contactName || p.contact?.name || p.contactName || livePhys.contactName || livePhys.contact_name || "";
                  const contactPhone = p.contactPhone || livePhys.contactPhone || livePhys.contact_phone || "";
                  const siteName = p.siteName || p.site_name || livePhys.siteName || livePhys.siteName || "";

                  if (specialty) updateSection("physician", "specialty", specialty);
                  if (tin) updateSection("physician", "tin", `${tin}`);
                  if (ptan) updateSection("physician", "ptan", `${ptan}`);
                  if (contactName) updateSection("physician", "contact", contactName);
                  if (contactPhone) updateSection("physician", "contactPhone", `${contactPhone}`);
                  if (siteName) updateSection("physician", "siteName", siteName);
                });
                setOpenPhysicianModal(true);
              }}
              sx={{
                bg: 'blue.200',
                border: '1px solid',
                borderColor: 'gray.200',
                _hover: { bg: 'gray.50' },
                borderRadius: 'md'
              }}
              >Select Physician</Button>
            }
          >
            <HStack>
              <StyledInput
                label="Physician Name"
                value={formData.physician?.name}
                onChange={(v) => updateSection("physician", "name", v)}
                isRequired
              />
              
              <StyledInput
                label="Phone"
                type="tel"
                inputMode="numeric"
                value={formData.physician?.phone}
                onChange={(v) => updateSection("physician", "phone", v)}
                isRequired
              />
              <StyledInput
                label="Fax"
                type="tel"
                inputMode="numeric"
                value={formData.physician?.fax}
                onChange={(v) => updateSection("physician", "fax", v)}
              />
            </HStack>
            <HStack>
              <StyledInput
                label="NPI"
                value={formData.physician?.npi}
                onChange={(v) => updateSection("physician", "npi", v)}
                isRequired
              />
              <StyledInput
                label="TIN"
                value={formData.physician?.tin}
                onChange={(v) => updateSection("physician", "tin", v)}
                isRequired
              />
              <StyledInput
                label="PTAN"
                value={formData.physician?.ptan}
                onChange={(v) => updateSection("physician", "ptan", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Contact Name"
                value={formData.physician?.contact}
                onChange={(v) => updateSection("physician", "contact", v)}
                isRequired
              />
              <StyledInput
                label="Contact Phone"
                value={formData.physician?.contactPhone}
                onChange={(v) => updateSection("physician", "contactPhone", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Street"
                value={formData.physician?.street}
                onChange={(v) => updateSection("physician", "street", v)}
                isRequired
              />
              <StyledInput
                label="City"
                value={formData.physician?.city}
                onChange={(v) => updateSection("physician", "city", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="State"
                value={formData.physician?.state}
                onChange={(v) => {
                  updateSection("physician", "state", v);
                  const mac = getMacFromState(v);
                  updateSection("physician", "mac", mac || "");
                }}
                isRequired
              />
              <StyledInput
                label="Zip Code"
                value={formData.physician?.zip}
                onChange={(v) => updateSection("physician", "zip", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Medicare Admin Contractor"
                value={formData.physician?.mac}
                onChange={(v) => updateSection("physician", "mac", v)}
                isRequired
                isDisabled
              />
              <StyledInput
                label="Jurisdiction"
                value={formData.physician?.jurisdiction}
                onChange={(v) => updateSection("physician", "jurisdiction", v)}
                isRequired
                isDisabled
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Specialty"
                value={formData.physician?.specialty}
                onChange={(v) => updateSection("physician", "specialty", v)}
                isRequired
              />
              <StyledInput
                label="Site Name (if different)"
                value={formData.physician?.siteName}
                onChange={(v) => updateSection("physician", "siteName", v)}
              />
            </HStack>
          </Section>

          {/* Patient */}
          <Section
            title="Patient Information"
            isOpen={SectionVisible("Patient")}
            isDone={completed.Patient}
            onDone={() => {
              const errors = validatePatient();
              if (errors.length > 0) {
                toast({
                  title: "Please complete all required fields",
                  description: `Missing: ${errors.join(", ")}`,
                  status: "warning",
                  duration: 7000,
                  isClosable: true,
                });
                return;
              }
              handleDone("Patient");
              setCurrentStep(3);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onEdit={() => setCurrentStep(2)}
            rightAddon={
              <Button size="sm" variant="ghost" isDisabled onClick={() => {
                BaaEntitySelectorModal.setOnSelect((p) => {
                  if (!p) return;
                  updateSection("patient", "name", p.name || p.patientName || "");
                  updateSection("patient", "dob", p.dob || p.dateOfBirth || "");
                  updateSection("patient", "phone", p.phone || "");
                  updateSection("patient", "street", p.street || "");
                  updateSection("patient", "city", p.city || "");
                  updateSection("patient", "state", p.state || "");
                  updateSection("patient", "zip", p.zip || "");
                });
                setOpenPatientModal(true);
              }}
              sx={{
                bg: 'blue.200',
                border: '1px solid',
                borderColor: 'gray.200',
                _hover: { bg: 'gray.50' },
                borderRadius: 'md'
              }}
              >Select Patient</Button>
            }
          >
            <HStack alignItems="flex-end">
              <FormControl isRequired>
                <FormLabel>Type of Submission</FormLabel>
                <RadioGroup
                  value={formData.formType || ""}
                  onChange={(v) =>
                    setFormData((prev) => ({ ...prev, formType: v }))
                  }
                  colorScheme="teal"
                >
                  <HStack spacing={6}>
                    <Radio value="New Patient" bg="white" borderColor="gray">
                      New Patient
                    </Radio>
                    <Radio value="Reverification" bg="white" borderColor="gray">
                      Reverification
                    </Radio>
                    <Radio value="Prior Authorization" bg="white" borderColor="gray">
                      Prior Authorization
                    </Radio>
                    <Radio value="New Insurance" bg="white" borderColor="gray">
                      New Insurance
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            </HStack>
            <HStack alignItems="flex-end">
              <StyledInput
                label="Patient Name"
                value={formData.patient?.name}
                onChange={(v) => updateSection("patient", "name", v)}
                onBlur={() => autofillInsuranceFromPatient()}
                isRequired
              />
              <StyledInput
                label="Date of Birth"
                type="date"
                value={formData.patient?.dob}
                onChange={(v) => updateSection("patient", "dob", v)}
                onBlur={() => autofillInsuranceFromPatient()}
                isRequired
              />
              <StyledInput
                label="Phone"
                type="tel"
                inputMode="numeric"
                value={formData.patient?.phone}
                onChange={(v) => updateSection("patient", "phone", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="Street"
                value={formData.patient?.street}
                onChange={(v) => updateSection("patient", "street", v)}
                isRequired
              />
              <StyledInput
                label="City"
                value={formData.patient?.city}
                onChange={(v) => updateSection("patient", "city", v)}
                isRequired
              />
            </HStack>
            <HStack>
              <StyledInput
                label="State"
                value={formData.patient?.state}
                onChange={(v) => updateSection("patient", "state", v)}
                isRequired
              />
              <StyledInput
                label="Zip Code"
                value={formData.patient?.zip}
                onChange={(v) => updateSection("patient", "zip", v)}
                isRequired
              />
            </HStack>
            <FormControl isRequired>
              <FormLabel>OK to Contact Patient</FormLabel>
              <RadioGroup
                value={formData.patient?.okToContact || ""}
                onChange={(v) => updateSection("patient", "okToContact", v)}
                colorScheme="teal"
              >
                <HStack spacing={6}>
                  <Radio value="Yes" bg="white" borderColor={"gray"}>
                    Yes
                  </Radio>
                  <Radio value="No" bg="white" borderColor={"gray"}>
                    No
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
          </Section>

          {/* Insurance */}
          <Section
            title="Insurance Information"
            isOpen={SectionVisible("Insurance")}
            isDone={completed.Insurance}
            onDone={() => {
              const errors = validateInsurance();
              if (errors.length > 0) {
                toast({
                  title: "Please complete all required fields",
                  description: `Missing: ${errors.join(", ")}`,
                  status: "warning",
                  duration: 7000,
                  isClosable: true,
                });
                return;
              }
              handleDone("Insurance");
              setCurrentStep(4);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onEdit={() => setCurrentStep(3)}
          >
            {formData.insurance.map((block, i) => (
              <Box
                key={i}
                position="relative"
                border="1px solid var(--button-border-color)"
                borderRadius="md"
                p={4}
                mt={3}
                bg="white"
              >
                <Text fontWeight="bold">
                  {["Primary", "Secondary", "Tertiary"][i]} Insurance
                </Text>
                <VStack spacing={4} align="stretch" mt={2}>
                  <HStack alignItems="flex-end">
                    <StyledInput
                      label="Subscriber Name"
                      placeholder="Subscriber Name"
                      value={block.subscriberName}
                      onChange={(v) => updateInsurance(i, "subscriberName", v)}
                      isRequired
                    />
                    <StyledInput
                      label="Subscriber Date of Birth"
                      placeholder="Subscriber DOB"
                      type="date"
                      value={block.subscriberDob}
                      onChange={(v) => updateInsurance(i, "subscriberDob", v)}
                      isRequired
                    />
                  </HStack>
                  <HStack>
                    <StyledInput
                      label="Insurance"
                      placeholder="Insurance"
                      value={block.name}
                      onChange={(v) => updateInsurance(i, "name", v)}
                      isRequired
                    />
                    <StyledInput
                      label="Policy Number"
                      placeholder="Policy Number"
                      value={block.policy}
                      onChange={(v) => updateInsurance(i, "policy", v)}
                      isRequired
                    />
                  </HStack>
                  <HStack>
                    <StyledInput
                      label="Plan Type"
                      placeholder="Plan Type (HMO/PPO/Other)"
                      value={block.planType}
                      onChange={(v) => updateInsurance(i, "planType", v)}
                      isRequired
                    />
                    <StyledInput
                      label="Insurance Phone Number"
                      placeholder="Insurance Phone Number"
                      type="tel"
                      inputMode="numeric"
                      value={block.phone}
                      onChange={(v) => updateInsurance(i, "phone", v)}
                      isRequired
                    />
                  </HStack>

                  <FormControl isRequired>
                    <FormLabel>Provider Participates in Network?</FormLabel>
                    <RadioGroup
                      value={block.inNetwork || ""}
                      onChange={(v) => updateInsurance(i, "inNetwork", v)}
                      colorScheme="teal"
                    >
                      <HStack spacing={6}>
                        {["Yes", "No", "Not Sure"].map((value) => (
                          <Radio key={value} value={value} bg="white" borderColor={"gray"}>
                            {value}
                          </Radio>
                        ))}
                      </HStack>
                    </RadioGroup>
                  </FormControl>
                </VStack>

                {i > 0 && (
                  <Button
                    onClick={() => handleRemoveInsurance(i)}
                    size="xs"
                    variant="outline"
                    mt={3}
                    _hover={{ transform: "scale(1)" }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}

            {formData.insurance.length < 3 && (
              <Button
                onClick={handleAddInsurance}
                size="sm"
                mt={4}
                _hover={{
                  color: "var(--primary-color)",
                  transform: "scale(1)",
                  bg: "gray.50",
                }}
              >
                + Add {formData.insurance.length === 1 ? "Secondary" : "Tertiary"} Insurance
              </Button>
            )}
          </Section>

          {/* Wounds */}
          <Box mt={8} borderRadius="lg" bg="gray.50" p={6} boxShadow="sm" borderWidth={1} borderColor="gray.200">
            <Flex justify="space-between" align="center" mb={4}>
              <Heading as="h2" size="lg">Wound Information</Heading>
            </Flex>

            <Box mb={4}>
              <Text mb={1} fontWeight="bold">Number of Wounds</Text>
              <Input
                size="sm"
                bg="white"
                type="number"
                min={1}
                max={10}
                value={numWoundsInput}
                onChange={(e) => {
                  if (!fromCalculator && !cloneFrom) setNumWoundsInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !fromCalculator && !cloneFrom) {
                    const parsed = parseInt(numWoundsInput.trim(), 10);
                    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
                      updateNumWounds(parsed.toString());
                    } else {
                      setNumWoundsInput(lastValidNumWounds.toString());
                    }
                  }
                }}
                onBlur={() => {
                  if (!fromCalculator && !cloneFrom) {
                    const parsed = parseInt(numWoundsInput.trim(), 10);
                    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
                      updateNumWounds(parsed.toString());
                    } else {
                      setNumWoundsInput(lastValidNumWounds.toString());
                    }
                  }
                }}
                isDisabled={fromCalculator || !!cloneFrom}
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                {fromCalculator
                  ? "Coming from the calculator limits this form to a single wound."
                  : !!cloneFrom
                  ? "Cloning a previous IVR limits this form to a single wound."
                  : "A separate IVR will be generated for each wound you document."}
              </Text>
            </Box>

            {(formData.wounds || []).map((wound, i) => (
              <WoundForm
                key={i}
                index={i}
                data={wound}
                onChange={updateWoundField}
                replaceWound={replaceWound}
                isOpen={selectedWoundIndex === i}
                isDone={!!completed[i]}
                onDone={() => handleWoundDone(i)}
                onEdit={() => handleWoundEdit(i)}
                onClearGraftInfo={handleClearGraftInfo}
              />
            ))}

            {errorMessages.length > 0 && (
              <Box mt={4}>
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" mb={1}>Validation Errors</Text>
                    <VStack align="start" spacing={1}>
                      {errorMessages.map((err, idx) => (
                        <Text key={idx} fontSize="sm">{err}</Text>
                      ))}
                    </VStack>
                  </Box>
                </Alert>
              </Box>
            )}

            <HStack spacing={4} mt={8}>
              <Button
                onClick={submitToServer}
                colorScheme="teal"
                isLoading={submitting}
                loadingText="Submitting…"
                _hover={{ transform: "scale(1)" }}
              >
                Create IVR
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                _hover={{ transform: "scale(1)" }}
              >
                Cancel
              </Button>
            </HStack>
          </Box>
        </>
      )}
    </Container>
  );
};

export default CreateIVR;
